import type { ServerConnection } from "@/context/server"
import { getDirectoryBootstrap } from "@/transport/directory-bootstrap"
import { getRuntimeBootstrap } from "@/transport/runtime-bootstrap"
import type { MuxClient, Session } from "@mux/sdk/v2/client"
import { createSignal } from "solid-js"
import type { SetStoreFunction, Store } from "solid-js/store"
import { bootstrapDirectory, bootstrapGlobal } from "./bootstrap"
import { createBootstrapGate } from "./bootstrap-gate"
import type { GlobalStore, State, VcsCache } from "./types"

type ChildStore = [Store<State>, SetStoreFunction<State>]

export function createGlobalBootstrapController(input: {
  globalClient: MuxClient
  connection: () => ServerConnection.HttpBase | undefined
  loadRuntimeBootstrap?: typeof getRuntimeBootstrap
  loadDirectoryBootstrap?: typeof getDirectoryBootstrap
  createClient: (directory: string) => MuxClient
  hasServer: () => boolean
  store: Store<GlobalStore>
  setStore: SetStoreFunction<GlobalStore>
  ensureChild: (directory: string) => ChildStore
  vcsCache: (directory: string) => VcsCache | undefined
  pin: (directory: string) => void
  unpin: (directory: string) => void
  loadSessions: (directory: string) => Promise<void>
  /** Installs each workspace's Oracle chat from the runtime bootstrap. */
  seedOracles: (oracles: Session[]) => void
  refreshStatuses: () => Promise<unknown>
  cacheGlobal: () => void
  requestFailedTitle: string
  translate: (key: string, vars?: Record<string, string | number>) => string
  formatMoreCount: (count: number) => string
  /** Loads every open question and permission ask on the machine. */
  refreshRequests: () => Promise<unknown>
}) {
  let globalTask: Promise<void> | undefined
  let queuedGlobalRefresh: Promise<void> | undefined
  let globalComplete = false
  // `ready` also turns true when the machine cannot be reached, so the UI does
  // not hang; `loaded` means a global snapshot actually came from the machine.
  const [loaded, setLoaded] = createSignal(false)
  const completedDirectories = new Set<string>()
  const queuedDirectoryRefreshes = new Map<string, Promise<void>>()
  const suspendedDirectories = new Map<string, number>()
  const deletedDirectories = new Set<string>()
  let active = true

  function canBootstrap(directory: string) {
    return active && !!directory && !suspendedDirectories.has(directory) && !deletedDirectories.has(directory)
  }

  function forgetDirectory(directory: string) {
    completedDirectories.delete(directory)
    queuedDirectoryRefreshes.delete(directory)
    gate.cancel(directory)
  }

  function runGlobal() {
    if (globalTask) return globalTask
    if (!input.hasServer()) {
      input.setStore("ready", true)
      globalComplete = true
      return Promise.resolve()
    }
    const connection = input.connection()
    if (!connection) {
      input.setStore("ready", true)
      return Promise.resolve()
    }
    globalTask = bootstrapGlobal({
      globalSDK: input.globalClient,
      connection,
      loadRuntimeBootstrap: input.loadRuntimeBootstrap,
      requestFailedTitle: input.requestFailedTitle,
      translate: input.translate,
      formatMoreCount: input.formatMoreCount,
      setGlobalStore: input.setStore,
      refreshRequests: input.refreshRequests,
      seedOracles: (oracles) => {
        if (active) input.seedOracles(oracles.filter((oracle) => !deletedDirectories.has(oracle.workspaceID ?? "")))
      },
      setProjects: (projects) => {
        if (active) input.setStore("project", projects.filter((project) => !deletedDirectories.has(project.id)))
      },
    }).then(async (result) => {
      if (!active) return
      if (result.criticalComplete) {
        setLoaded(true)
        input.cacheGlobal()
      }
      const backgroundComplete = await result.background
      if (!active) return
      globalComplete = result.criticalComplete && backgroundComplete
      if (globalComplete) input.cacheGlobal()
    }).finally(() => {
      globalTask = undefined
    })
    return globalTask
  }

  function ensureGlobal() {
    if (globalComplete) return Promise.resolve()
    return runGlobal()
  }

  function refreshGlobal() {
    globalComplete = false
    if (!globalTask) return runGlobal()
    if (queuedGlobalRefresh) return queuedGlobalRefresh
    queuedGlobalRefresh = globalTask.then(runGlobal).finally(() => {
      queuedGlobalRefresh = undefined
    })
    return queuedGlobalRefresh
  }

  const gate = createBootstrapGate(async (directory, signal) => {
    input.pin(directory)
    try {
      // The workspace's own snapshot needs nothing from the machine-wide one, so
      // it waits for it only when no workspaces are known yet, cached or loaded.
      if (!input.store.ready) {
        const global = ensureGlobal()
        if (!input.store.project.length) await global
      }
      if (signal.aborted || !canBootstrap(directory)) return
      const child = input.ensureChild(directory)
      const cache = input.vcsCache(directory)
      if (!cache) return
      const result = await bootstrapDirectory({
        directory,
        signal,
        connection: input.connection()!,
        loadDirectoryBootstrap: input.loadDirectoryBootstrap,
        sdk: input.createClient(directory),
        store: child[0],
        setStore: child[1],
        vcsCache: cache,
        loadSessions: input.loadSessions,
        translate: input.translate,
        globalStore: input.store,
        setGlobalStore: input.setStore,
        refreshSessionStatuses: async () => {
          await input.refreshStatuses()
        },
      })
      const metadataComplete = await result.metadata
      if (!signal.aborted && result.criticalComplete && metadataComplete) completedDirectories.add(directory)
    } finally {
      input.unpin(directory)
    }
  })

  function ensureDirectory(directory: string) {
    if (!canBootstrap(directory) || completedDirectories.has(directory)) return Promise.resolve()
    return gate.bootstrap(directory)
  }

  function refreshDirectory(directory: string) {
    if (!canBootstrap(directory)) return Promise.resolve()
    completedDirectories.delete(directory)
    if (!gate.isPending(directory)) return gate.bootstrap(directory)
    const existing = queuedDirectoryRefreshes.get(directory)
    if (existing) return existing
    const refresh = gate.bootstrap(directory).then(() => {
      if (queuedDirectoryRefreshes.get(directory) !== refresh || !canBootstrap(directory)) return
      completedDirectories.delete(directory)
      return gate.bootstrap(directory)
    }).finally(() => {
      if (queuedDirectoryRefreshes.get(directory) === refresh) queuedDirectoryRefreshes.delete(directory)
    })
    queuedDirectoryRefreshes.set(directory, refresh)
    return refresh
  }

  return {
    ensureGlobal,
    refreshGlobal,
    loaded,
    ensureDirectory,
    refreshDirectory,
    forgetDirectory,
    suspendDirectory(directory: string) {
      suspendedDirectories.set(directory, (suspendedDirectories.get(directory) ?? 0) + 1)
      forgetDirectory(directory)
      let released = false
      return (outcome: "deleted" | "retry" | "cancelled") => {
        if (released) return
        released = true
        if (outcome === "deleted" && active) deletedDirectories.add(directory)
        const remaining = (suspendedDirectories.get(directory) ?? 1) - 1
        if (remaining > 0) suspendedDirectories.set(directory, remaining)
        else suspendedDirectories.delete(directory)
        if (outcome === "retry") void ensureDirectory(directory)
      }
    },
    isPending: gate.isPending,
    dispose() {
      active = false
      gate.dispose()
      completedDirectories.clear()
      queuedDirectoryRefreshes.clear()
      suspendedDirectories.clear()
      deletedDirectories.clear()
    },
  }
}
